'use client'

import { useMemo, ReactNode, useCallback, useState, createContext, useContext } from 'react'
import { WalletProvider } from '@tronweb3/tronwallet-adapter-react-hooks'
import { WalletModalProvider } from '@tronweb3/tronwallet-adapter-react-ui'
import { WalletError, WalletNotFoundError, WalletDisconnectedError } from '@tronweb3/tronwallet-abstract-adapter'
import toast from 'react-hot-toast'

// Import only the specific adapters we need (avoid importing all from tronwallet-adapters)
// This avoids the WalletConnect adapter which has bundler issues with thread-stream/pino
import { TronLinkAdapter } from '@tronweb3/tronwallet-adapter-tronlink'
import { LedgerAdapter } from '@tronweb3/tronwallet-adapter-ledger'
import { BitKeepAdapter } from '@tronweb3/tronwallet-adapter-bitkeep'
import { OkxWalletAdapter } from '@tronweb3/tronwallet-adapter-okxwallet'
import { TokenPocketAdapter } from '@tronweb3/tronwallet-adapter-tokenpocket'

// Import TronWallet UI styles (for the official wallet modal)
import '@tronweb3/tronwallet-adapter-react-ui/style.css'

/**
 * Tron Wallet Provider with LAZY Adapter Loading
 * 
 * IMPORTANT: Adapters are NOT created until user explicitly wants to connect.
 * This prevents TronLink from showing connection popups on page load.
 * 
 * The pattern:
 * 1. Page loads -> empty adapter list (no TronLink popups)
 * 2. User clicks "Connect Tron" -> enableAdapters() called
 * 3. Adapters are created -> modal shows wallet options
 */

interface TronProviderProps {
    children: ReactNode
}

// Context to track if adapters should be loaded
interface TronContextType {
    adaptersEnabled: boolean
    enableAdapters: () => void
}

const TronContext = createContext<TronContextType>({
    adaptersEnabled: false,
    enableAdapters: () => { },
})

export function useTronContext() {
    return useContext(TronContext)
}

/**
 * Create adapters only when called (lazy loading)
 * Uses individual adapter imports to avoid bundler issues with WalletConnect
 */
function createTronAdapters() {
    try {
        const adapterList = [
            // Primary wallet - TronLink
            // openTronLinkAppOnMobile: false - Don't auto-open TronLink app on mobile
            // openUrlWhenWalletNotFound: false - Don't redirect to install page automatically
            new TronLinkAdapter({
                openTronLinkAppOnMobile: false,
                openUrlWhenWalletNotFound: false,
            }),
            // Hardware wallet
            new LedgerAdapter({ accountNumber: 2 }),
            // Popular Tron wallets (limited set to avoid bundler issues)
            new BitKeepAdapter(),
            new OkxWalletAdapter(),
            new TokenPocketAdapter(),
        ]

        return adapterList
    } catch (error) {
        console.error('[TronProvider] Error initializing adapters:', error)
        return [new TronLinkAdapter({
            openTronLinkAppOnMobile: false,
            openUrlWhenWalletNotFound: false,
        })]
    }
}

export function TronProvider({ children }: TronProviderProps) {
    // Adapters start as disabled (empty array)
    // They only get created when user wants to connect
    const [adaptersEnabled, setAdaptersEnabled] = useState(false)

    const enableAdapters = useCallback(() => {
        setAdaptersEnabled(true)
    }, [])

    // Only create adapters when enabled (user wants to connect)
    const adapters = useMemo(() => {
        // Skip during SSR
        if (typeof window === 'undefined') {
            return []
        }

        // Don't create adapters until user wants to connect
        // This prevents TronLink from requesting connection on page load
        if (!adaptersEnabled) {
            return []
        }

        return createTronAdapters()
    }, [adaptersEnabled])

    // Error handler for wallet connection issues
    const onError = useCallback((error: WalletError) => {
        if (error instanceof WalletNotFoundError) {
            toast.error(`Wallet not found: ${error.message}`)
        } else if (error instanceof WalletDisconnectedError) {
            toast.error('Wallet disconnected')
        } else {
            toast.error(`Wallet error: ${error.message}`)
        }
        console.error('[TronProvider] Wallet error:', error)
    }, [])

    return (
        <TronContext.Provider value={{ adaptersEnabled, enableAdapters }}>
            <WalletProvider
                adapters={adapters}
                onError={onError}
                autoConnect={false}
                disableAutoConnectOnLoad={true}
            >
                <WalletModalProvider>
                    {children}
                </WalletModalProvider>
            </WalletProvider>
        </TronContext.Provider>
    )
}

// Re-export hooks for convenience
export { useWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
